import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { rbacApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShieldCheck, Plus, Trash2, Check } from 'lucide-react';

const SCOPE_LABELS = { all: 'All units', unit: 'One unit', unit_department: 'One unit + department' };
const COLS = [
  { id: 'view', label: 'View' },
  { id: 'create', label: 'Create' },
  { id: 'edit', label: 'Edit' },
  { id: 'delete', label: 'Delete' },
  { id: 'extra', label: 'Special' },
];

export default function RbacSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sel, setSel] = useState('');
  const [perms, setPerms] = useState(new Set());
  const [scope, setScope] = useState('unit');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', copy_from: '', scope: 'unit' });

  const load = async () => {
    try {
      const [c, r] = await Promise.all([rbacApi.catalog(), rbacApi.roles()]);
      setCatalog(c.data.catalog || []);
      setRoles(r.data || []);
      if (!sel && r.data?.length) selectRole(r.data[0], r.data);
    } catch { toast.error('Could not load RBAC settings'); }
  };

  useEffect(() => {
    if (user && user.role !== 'admin' && !(user.permissions || []).includes('rbac.manage')) { navigate('/', { replace: true }); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const selectRole = (r) => {
    if (dirty && !window.confirm('Discard unsaved changes to the current role?')) return;
    setSel(r.role); setPerms(new Set(r.permissions || [])); setScope(r.scope || 'unit'); setDirty(false);
  };

  const current = useMemo(() => roles.find((r) => r.role === sel), [roles, sel]);
  const isAdminRole = sel === 'admin';

  const toggle = (key) => {
    if (isAdminRole) return;
    setPerms((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    setDirty(true);
  };

  const save = async () => {
    if (!sel || isAdminRole) return;
    setBusy(true);
    try {
      await rbacApi.updateRole(sel, { permissions: [...perms], scope });
      toast.success(`Role "${sel}" saved`); setDirty(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) { toast.error('Enter a role name'); return; }
    setBusy(true);
    try {
      const r = await rbacApi.createRole({ role: newRole.name, scope: newRole.scope, copy_from: newRole.copy_from || null, permissions: [] });
      toast.success(`Role "${r.data.role}" created`); setAddOpen(false); setNewRole({ name: '', copy_from: '', scope: 'unit' });
      await load(); setSel(r.data.role); setPerms(new Set(r.data.permissions || [])); setScope(r.data.scope); setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create role'); }
    finally { setBusy(false); }
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Delete role "${r.role}"? This cannot be undone.`)) return;
    try { await rbacApi.deleteRole(r.role); toast.success('Role deleted'); if (sel === r.role) setSel(''); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete role'); }
  };

  const cellFor = (mod, colId) => {
    const items = (mod.perms || []).filter((p) => p.col === colId);
    if (items.length === 0) return <span className="text-[#D1D7E2]">—</span>;
    return (
      <div className="flex flex-col items-center gap-1">
        {items.map((p) => {
          const on = perms.has(p.key) || isAdminRole;
          return (
            <button key={p.key} data-testid={`perm-${sel}-${p.key}`} onClick={() => toggle(p.key)} disabled={isAdminRole}
              title={p.key}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium border transition-all
                ${on ? 'bg-[#2563EB] border-[#2563EB] text-white' : 'bg-white border-[#D1D7E2] text-[#98A1B2] hover:border-[#2563EB]'}
                ${isAdminRole ? 'opacity-70 cursor-not-allowed' : ''}`}>
              {on && <Check className="w-3 h-3" strokeWidth={3} />}
              {p.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="rbac-settings-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1300px] mx-auto">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg,#15307F,#2563EB)' }}>
                <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Roles &amp; Permissions</h1>
                <p className="text-sm text-[#5A6377]">Configure what each role can see and do. Changes apply on the user&apos;s next request.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Roles rail */}
              <div className="bg-white border border-[#E6E9F0] rounded-[14px] p-4 h-fit">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-[#11182B]">Roles</h2>
                  <button data-testid="add-role-button" onClick={() => setAddOpen(true)} className="p-1.5 rounded-md hover:bg-[#F7F8FB] text-[#2563EB]"><Plus className="w-4 h-4" /></button>
                </div>
                <div className="space-y-1.5">
                  {roles.map((r) => (
                    <div key={r.role} data-testid={`role-item-${r.role}`} onClick={() => selectRole(r)}
                      className={`group flex items-center justify-between rounded-[10px] px-3 py-2.5 cursor-pointer border transition-all
                        ${sel === r.role ? 'text-white border-transparent' : 'bg-white border-[#E6E9F0] hover:border-[#2563EB]'}`}
                      style={sel === r.role ? { background: 'linear-gradient(135deg,#15307F,#2563EB)' } : {}}>
                      <div className="min-w-0">
                        <div className={`text-sm font-semibold truncate ${sel === r.role ? '' : 'text-[#11182B]'}`}>{r.role}</div>
                        <div className={`text-[10px] ${sel === r.role ? 'text-[#AFC0E8]' : 'text-[#98A1B2]'}`}>{SCOPE_LABELS[r.scope] || r.scope} · {r.users} user{r.users === 1 ? '' : 's'}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {r.is_system && <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full ${sel === r.role ? 'bg-white/20 text-white' : 'bg-[#E8EFFD] text-[#15307F]'}`}>System</span>}
                        {!r.is_system && (
                          <button data-testid={`delete-role-${r.role}`} onClick={(e) => { e.stopPropagation(); removeRole(r); }}
                            className={`opacity-0 group-hover:opacity-100 p-1 rounded ${sel === r.role ? 'text-white' : 'text-[#E11D2A]'}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Matrix */}
              <div className="lg:col-span-3 bg-white border border-[#E6E9F0] rounded-[14px] p-5">
                {!current ? <div className="text-sm text-[#5A6377]">Select a role.</div> : (
                  <>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-[#11182B]">{current.role}{current.is_system ? ' · System' : ''}</h2>
                        <p className="text-xs text-[#5A6377]">{isAdminRole ? 'Full access by design — not editable.' : 'Tick the actions this role may perform.'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select data-testid="role-scope" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2"
                                value={scope} disabled={isAdminRole} onChange={(e) => { setScope(e.target.value); setDirty(true); }}>
                          <option value="all">Scope: All units</option>
                          <option value="unit">Scope: One unit</option>
                          <option value="unit_department">Scope: One unit + department</option>
                        </select>
                        <Button data-testid="save-role-button" onClick={save} disabled={busy || !dirty || isAdminRole}>{busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}</Button>
                      </div>
                    </div>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="text-xs uppercase text-[#5A6377]">
                          <th className="text-left py-2 pr-3 border-b border-[#E6E9F0]">Module</th>
                          {COLS.map((c) => <th key={c.id} className="text-center py-2 px-3 border-b border-[#E6E9F0]">{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.map((mod) => (
                          <tr key={mod.module} className="border-b border-[#F0F2F7] last:border-0 hover:bg-[#F7F8FB]">
                            <td className="py-3 pr-3 font-medium text-[#11182B]">{mod.module}</td>
                            {COLS.map((c) => <td key={c.id} className="py-2 px-3 text-center align-middle">{cellFor(mod, c.id)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Add role dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New role</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Role name</Label>
              <Input data-testid="new-role-name" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} placeholder="e.g. plant_head" /></div>
            <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Copy permissions from (optional)</Label>
              <select data-testid="new-role-copy" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                      value={newRole.copy_from} onChange={(e) => setNewRole({ ...newRole, copy_from: e.target.value })}>
                <option value="">— start empty —</option>
                {roles.filter((r) => r.role !== 'admin').map((r) => <option key={r.role} value={r.role}>{r.role}</option>)}
              </select></div>
            <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Scope</Label>
              <select data-testid="new-role-scope" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                      value={newRole.scope} onChange={(e) => setNewRole({ ...newRole, scope: e.target.value })}>
                <option value="all">All units</option>
                <option value="unit">One unit</option>
                <option value="unit_department">One unit + department</option>
              </select></div>
            <div className="flex justify-end"><Button data-testid="new-role-create" onClick={createRole} disabled={busy}>Create role</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
